import {
  array,
  bool,
  func,
  number,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types'
import { Fragment, useState } from 'react'
import Dropzone from 'react-dropzone'
import { Icon } from '@opallabs/lapidary'
import { AnalyticsConsumer, AnalyticsLayer } from '@opallabs/quanta'
import { kuiperClassNames } from '../utils'
import './styles.scss'
import * as buttonStyles from './buttonUploaderStyles'
import ImagePreview from './ImagePreview'
import {
  flattenAcceptedFileTypes,
  joinAcceptedFileTypes,
  defineAllFileTypes,
} from './utils.js'

defineAllFileTypes()

const onDropAccepted = (
  acceptedFiles,
  uploadInstructions,
  setFileName,
  track
) => {
  track('Drop')

  acceptedFiles.forEach((file) => {
    uploadInstructions(file)
    setFileName(file.name)
  })
}

const flattenedFileTypes = (allowableFiles) =>
  flattenAcceptedFileTypes(allowableFiles)

/**
 * @note This component is not intended to be used directly. Use the
 *   sub-types located in this directory instead.
 * @private
 */
const Uploader = ({
  allowableFiles,
  customStyles,
  disabled,
  htmlId,
  iconName,
  iconStyles = {},
  previewSrc,
  promptText,
  show,
  selected,
  setSelectedAsset,
  subText,
  theme = 'light',
  type,
  uploadInstructions,
}) => {
  const [fileName, setFileName] = useState('Your uploaded image')

  const { height: iconBlockSize, width: iconInlineSize } = iconStyles

  const UploadPrompt = (
    <Fragment>
      <Icon
        ariaHidden
        glyph={iconName}
        height={iconBlockSize}
        styleOverrides={{ container: buttonStyles.icon(type) }}
        useHypernova={type === 'image-preview'}
        width={iconInlineSize}
      />
      <div
        htmlFor="dropzone-input"
        className={kuiperClassNames('text')}
        aria-hidden={true}
      >
        {promptText}
        {subText}
      </div>
    </Fragment>
  )

  const Preview = (
    <ImagePreview
      previewSrc={previewSrc}
      uploadInstructions={uploadInstructions}
      selected={selected}
      setSelectedAsset={setSelectedAsset}
      fileName={fileName}
    />
  )

  return (
    <AnalyticsLayer name="Uploader">
      <AnalyticsConsumer>
        {({ track }) => (
          <div
            className={kuiperClassNames(
              'dropzone-wrapper',
              type === 'button' ? null : type,
              { show }
            )}
            css={customStyles}
            id={htmlId}
          >
            <Dropzone
              className={kuiperClassNames(
                'dropzone-upload-region',
                type === 'button' ? null : type,
                { enabled: !disabled }
              )}
              accept={joinAcceptedFileTypes(flattenedFileTypes(allowableFiles))}
              inputProps={{ id: 'dropzone-input', 'aria-hidden': true }}
              onDropAccepted={(acceptedFiles) =>
                onDropAccepted(
                  acceptedFiles,
                  uploadInstructions,
                  setFileName,
                  track
                )
              }
              disabled={disabled}
              css={
                type === 'button'
                  ? buttonStyles.uploader({
                      disabled,
                      theme,
                    })
                  : null
              }
            >
              {previewSrc ? Preview : UploadPrompt}
            </Dropzone>
          </div>
        )}
      </AnalyticsConsumer>
    </AnalyticsLayer>
  )
}

Uploader.propTypes = {
  allowableFiles: array,
  customStyles: object,
  disabled: bool,
  htmlId: string,
  iconName: string,
  iconStyles: shape({
    height: number,
    width: number,
  }),
  previewSrc: string,
  promptText: oneOfType([object, string]),
  selected: bool,
  setSelectedAsset: func,
  show: bool,
  subText: oneOfType([object, string]),
  theme: oneOf(['dark', 'light']),
  type: oneOf(['area', 'button', 'link', 'image-preview']),
  uploadInstructions: func,
}

Uploader.defaultProps = {
  allowableFiles: ['all'],
  disabled: false,
  selected: false,
}

export default Uploader
